import {
  getCachedDocUserData,
  updateDocsUserDataCache,
  updateUserDocsCache,
} from '@/helpers/cache'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import type {
  DocContent,
  DocUserData,
  DocWithContent,
} from '@/models/interfaces'
import { backend } from '@/services/backend'
import { saveReadingPosition } from '@/services/readingProgress'
import { findChapterAtPosition } from './chapter-timing'
import type { NativePlaybackProgress } from './native-audio.types'

type NativePlaybackChapter =
  NativePlaybackProgress['document']['chapters'][number]

interface NativeReadingPosition {
  chapterSlug: string
  percent: number
}

interface AppliedNativeReading extends NativeReadingPosition {
  userId: string
  docId: string
}

// A map from a native progress UUID to the reading position applied from that
// record. For example:
//     "550E8400-E29B-41D4-A716-446655440000" ->
//          { userId, docId, chapterSlug, percent }
//
// The swift plugin generates a UUID for each update record, and we use it on
// the javascript side to prevent retried requests from overwriting newer
// updates.
type AppliedNativeReadings = Record<string, AppliedNativeReading>

function appliedNativeReadings(): AppliedNativeReadings {
  const value = storage.getItem(StorageKeys.nativeProgressApplied)
  if (!value) {
    return {}
  }
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

function appliedNativeReading(
  progress: NativePlaybackProgress,
): AppliedNativeReading | undefined {
  const applied = appliedNativeReadings()[progress.id]
  return applied?.userId === progress.document.userId &&
    applied.docId === progress.document.docId
    ? applied
    : undefined
}

function rememberAppliedNativeReading(
  progress: NativePlaybackProgress,
  reading: NativeReadingPosition,
): void {
  const applied = appliedNativeReadings()
  applied[progress.id] = {
    userId: progress.document.userId,
    docId: progress.document.docId,
    ...reading,
  }
  storage.setItem(StorageKeys.nativeProgressApplied, JSON.stringify(applied))
}

export function forgetAppliedNativeReading(id: string): void {
  const applied = appliedNativeReadings()
  delete applied[id]
  storage.setItem(StorageKeys.nativeProgressApplied, JSON.stringify(applied))
}

function readingMatches(
  user: DocUserData,
  reading: NativeReadingPosition,
): boolean {
  return (
    user.position_chapter === reading.chapterSlug &&
    Math.round(user.position_percent) === reading.percent
  )
}

function readingPosition(progress: NativePlaybackProgress):
  | {
      chapter: NativePlaybackChapter
      percent: number
    }
  | undefined {
  const chapter = findChapterAtPosition(
    progress.document.chapters,
    progress.position,
  )
  if (!chapter) {
    return
  }
  const end = chapter.end ?? progress.duration
  const percent =
    end > chapter.start
      ? Math.round(
          Math.min(
            100,
            Math.max(
              0,
              (100 * (progress.position - chapter.start)) /
                (end - chapter.start),
            ),
          ),
        )
      : 0
  return { chapter, percent }
}

export function canApplyNativeReadingPosition(
  progress: NativePlaybackProgress,
  user: DocUserData,
): boolean {
  const applied = appliedNativeReading(progress)
  return !applied || readingMatches(user, applied)
}

/** Cache native progress so that a guide reopens at the right place offline. */
export async function cacheNativeProgressLocally(
  progress: NativePlaybackProgress,
  doc: DocWithContent,
  updateReadingPosition: boolean,
  isOriginatingUser: () => boolean,
): Promise<void> {
  if (doc.id !== progress.document.docId || !isOriginatingUser()) {
    return
  }
  doc.user.audio_position = Math.floor(progress.position)
  const reading = updateReadingPosition ? readingPosition(progress) : undefined
  let appliedReading: NativeReadingPosition | undefined
  if (reading) {
    const chapter = doc.content.find((page) => page.id === reading.chapter.id)
    if (chapter) {
      doc.user.position_chapter = chapter.url_slug
      doc.user.position_percent = reading.percent
      appliedReading = {
        chapterSlug: chapter.url_slug,
        percent: reading.percent,
      }
    }
  }
  await updateDocsUserDataCache(doc.id, doc.user, isOriginatingUser)
  if (!isOriginatingUser()) {
    return
  }
  await updateUserDocsCache(doc, isOriginatingUser)
  if (!isOriginatingUser()) {
    return
  }
  if (appliedReading) {
    rememberAppliedNativeReading(progress, appliedReading)
  }
}

/** Send a native audio position to the backend as a reading position. */
export async function sendNativeReadingPosition(
  progress: NativePlaybackProgress,
  currentDoc: DocWithContent | undefined,
  assertCurrentUser: () => void,
): Promise<void> {
  const reading = readingPosition(progress)
  if (!reading) {
    return
  }
  const chapter = currentDoc?.content.find(
    (page) => page.id === reading.chapter.id,
  )
  if (currentDoc && chapter) {
    const target = { chapterSlug: chapter.url_slug, percent: reading.percent }
    if (!readingMatches(currentDoc.user, target)) {
      return
    }
    await saveReadingPosition(currentDoc, chapter, reading.percent, {
      forceBackendSave: true,
    })
  } else {
    const applied = appliedNativeReading(progress)
    if (applied) {
      const cached = await getCachedDocUserData(progress.document.docId)
      assertCurrentUser()
      if (cached && !readingMatches(cached, applied)) {
        return
      }
    }
    await backend.saveReadingPosition(
      { id: reading.chapter.id } as DocContent,
      reading.percent,
    )
  }
  assertCurrentUser()
}
